import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import classnames from 'classnames/bind';

import ROUTES from '@CONSTANTS/ROUTES.constant';
import GenerationSteps from '@CONSTANTS/GENERATION_STEPS.constant';

import { OptionsStep, OptionsStepData } from '@REDUCERS';
import useDocumentGenerationSteps from '@HOOKS/store/useDocumentGenerationSteps';

import TabRadio from '@COMPONENTS/COMMON/inputs/radio/TabRadio';
import BasicInput from '@COMPONENTS/COMMON/inputs/input/BasicInput';
import StepNavigation from '@COMPONENTS/COMMON/navigation/StepNavigation';

import styles from './Options.module.scss';

const cx: CX = classnames.bind(styles);

export enum GenerationMode {
    FULL = 1,
    PARTIAL = 2,
}

export const generationModes = [
    {
        id: GenerationMode.FULL,
        name: 'Full Document',
    },
    {
        id: GenerationMode.PARTIAL,
        name: 'Partial Extraction',
    },
];

function OptionsPage() {
    const {
        actions: documentGenerationStepsActions,
        services: documentGenerationStepsServices,
    } = useDocumentGenerationSteps(() => true);

    const step: OptionsStep = documentGenerationStepsServices.getStepData(GenerationSteps.OPTIONS);

    const [
        generationMode,
        setGenerationMode,
    ] = useState(step.data?.id || GenerationMode.FULL);

    const [
        propertyName,
        setPropertyName,
    ] = useState(step.data?.propertyName || '');

    const navigate = useNavigate();

    const handleNext = () => {
        const data: OptionsStepData = {
            id: generationMode,
            propertyName,
        };

        documentGenerationStepsActions.apply({
            id: GenerationSteps.OPTIONS,
            isComplete: true,
            data,
        });

        // Navigate to select-content only if partial extraction is selected
        if (generationMode === GenerationMode.PARTIAL) {
            navigate(ROUTES.selectContent.path);
        } else {
            navigate(ROUTES.downloadDocument.path);
        }
    };

    return (
        <div className={cx('page')}>
            <h1>Options</h1>
            <div className={cx('content')}>
                <div className={cx('tabs-wrapper')}>
                    <TabRadio
                        title="What kind of document do you want to generate?"
                        items={generationModes}
                        activeId={generationMode}
                        onChange={(id: number) => {
                            setGenerationMode(id);
                        }}
                    />
                </div>

                <div className={cx('input-wrapper')}>
                    <BasicInput
                        value={propertyName}
                        label="Property Name (Optional)"
                        maxLength={40}
                        onChange={(val: string) => {
                            setPropertyName(val);
                        }}
                    />
                </div>
            </div>

            <StepNavigation
                onNext={handleNext}
                nextText={generationMode === GenerationMode.PARTIAL ? 'Continue to Content Selection' : 'Continue to Download'}
                sticky
            />
        </div>
    );
}

export default OptionsPage;
